/**
 * API endpoints from backend OpenAPI spec (api.json).
 * Paths are relative to API baseURL (e.g. http://localhost:3000/api).
 */
export const API_ENDPOINTS = {
    // ----- Health -----
    /** GET - Health check */
    HEALTH: "/api/health",

    // ----- Tax year -----
    /** GET - Supported years + default (operationId: getTaxYearConfig) */
    TAX_YEAR_CONFIG: "/api/tax-year",
    /** GET - Default tax year (operationId: getDefaultTaxYear) */
    TAX_YEAR_DEFAULT: "/api/tax-year/default",

    // ----- Auth -----
    /** POST - Sign up (SignupRequest) */
    SIGNUP: "/api/auth/signup",
    /** POST - Login (LoginRequest), sets session cookie */
    LOGIN: "/api/auth/login",
    /** POST - Send login OTP (SendOtpRequest) */
    SEND_OTP: "/api/auth/send-otp",
    /** POST - Resend login OTP (SendOtpRequest); 429 if < 1 min */
    RESEND_OTP: "/api/auth/resend-otp",
    /** POST - Verify login OTP (VerifyOtpRequest), sets session cookie */
    VERIFY_OTP: "/api/auth/verify-otp",
    /** GET - Verify email, query: token, userId, tenantId (and any other params from link) */
    VERIFY_EMAIL: "/api/auth/verify-email",
    /** POST - Forgot password (ForgotPasswordRequest) */
    FORGOT_PASSWORD: "/api/auth/forgot-password",
    /** POST - Reset password with email link token (ResetPasswordRequest) */
    RESET_PASSWORD: "/api/auth/reset-password",
    /** GET - Get registration user info, query: token, userId (and any other params from link) */
    GET_REGISTRATION_USER_INFO: "/api/auth/get-registration-user-info",
    /** POST - Complete registration (CompleteRegistrationRequest) */
    COMPLETE_REGISTRATION: "/api/auth/complete-registration",
    // ----- Profile (current user) -----
    /** GET - Get current user; POST - Update profile (UpdateProfileRequest) */
    ME: "/api/me",
    /** POST - Update password (UpdatePasswordRequest) */
    ME_PASSWORD: "/api/me/password",
    /** POST - Logout */
    ME_LOGOUT: "/api/me/logout",

    // ----- Docs (document management) -----
    /** GET - List my documents (getDocuments); POST - Presigned upload URLs (uploadDocuments, UploadDocumentsRequest) */
    DOCS: "/api/docs",
    /** GET - Document preview URL (getDocumentPreview); DELETE - Delete document (deleteDocument) */
    DOC_BY_ID: (documentId: string) =>
        `/api/docs/${encodeURIComponent(documentId)}`,
    /** POST - Confirm S3 upload completed */
    DOC_CONFIRM_UPLOAD: (documentId: string) =>
        `/api/docs/${encodeURIComponent(documentId)}/confirm-upload`,
    /** POST - Mark upload failed and soft-delete pending metadata */
    DOC_FAIL_UPLOAD: (documentId: string) =>
        `/api/docs/${encodeURIComponent(documentId)}/fail-upload`,

    // ----- Tax Filings (user portal) -----
    /** GET - List tax summaries for tax year */
    TAX_SUMMARIES: "/api/tax-summaries",
    /** GET - Tax summary preview URL */
    TAX_SUMMARY_BY_ID: (documentId: string) =>
        `/api/tax-summaries/${encodeURIComponent(documentId)}`,

    /** GET - List draft copies; PUT - Approve/reject draft */
    DRAFT_COPIES: "/api/draft-copies",
    /** GET - Draft copy preview; PUT - Update status */
    DRAFT_COPY_BY_ID: (documentId: string) =>
        `/api/draft-copies/${encodeURIComponent(documentId)}`,

    /** GET - List declarations; POST - Upload signed declaration */
    DECLARATIONS: "/api/declarations",
    /** GET - Declaration preview URL */
    DECLARATION_BY_ID: (documentId: string) =>
        `/api/declarations/${encodeURIComponent(documentId)}`,
    /** POST - Confirm signed declaration upload */
    DECLARATION_CONFIRM_UPLOAD: (documentId: string) =>
        `/api/declarations/${encodeURIComponent(documentId)}/confirm-upload`,
    /** POST - Fail signed declaration upload */
    DECLARATION_FAIL_UPLOAD: (documentId: string) =>
        `/api/declarations/${encodeURIComponent(documentId)}/fail-upload`,

    /** GET - List final copies */
    FINAL_COPIES: "/api/final-copies",
    /** GET - Final copy preview URL */
    FINAL_COPY_BY_ID: (documentId: string) =>
        `/api/final-copies/${encodeURIComponent(documentId)}`,

    // ----- Forms (tax form instances: individual / business) -----
    /** GET - List form instances (query: taxYear or beforeTaxYear required, filingType?); POST - Create form (FormCreateRequest) */
    FORMS: "/api/forms",
    /** GET - Get form by id; PUT - Update form (FormUpdateRequest) */
    FORM_BY_ID: (id: string) => `/api/forms/${encodeURIComponent(id)}`,
} as const;
